import React, { useEffect, useMemo, useState } from 'react';
import styles from './BulkAssignmentView.module.scss';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../common/Dropdown/Dropdown';
import SearchDropdown from '../../common/SearchDropdown/SearchDropdown';
import Button from '../../common/Button/Button';
import ToastMessage from '../../common/ToastMessage/ToastMessage';
import { toastMessageType } from '../../../types/types';
import { useNavigate } from 'react-router-dom';
import {
  useAssignABOMutation,
  useGetAssigneeListQuery,
  useGetAssignmentTypesQuery,
} from '../../../redux/services/aboAssignmentAPI';
import { useProfileParams } from '../../../utils/common';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';

export interface AwardInfo {
  awardCode: string;
  awardName: string;
  awardTagCode?: number;
  awardTagName?: string;
}

const roleListMock: DropdownItem[] = [
  { code: 1, value: 'assignment_types.primary_acc_manager' },
  { code: 2, value: 'assignment_types.case_evaluator' },
  { code: 3, value: 'assignment_types.both' },
  { code: 4, value: 'assignment_types.additional_acc_manager' },

];

const PermissionGroupTypeMapByRole: Record<string, string> = {
  '1': '3,5',
  '2': '4,6,7',
  '3': '3,4,5,6,7',
  '4': '1,2,3,4,5,6,7',
};

interface BulkAssignmentProps {
  selectedABOs: Array<{ aboNumber: string; affiliateCode: string }>;
  resetSelection: () => void;
  onAssignCallback: () => void;
}

const BulkAssignmentView: React.FC<BulkAssignmentProps> = ({
  selectedABOs,
  resetSelection,
  onAssignCallback,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedRole, setSelectedRole] = useState<DropdownItem | undefined>();
  const [selectedAssignee, setSelectedAssignee] = useState<DropdownItem | null>(null);

  const userDetails = useAppSelector((state: RootState) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);

  const [isAccessibleAssignmentSection] = useState(userDetails?.isAccessibleAssignmentSection || 0);
  const { role } = useProfileParams();

  const { data: assignmentTypes } = useGetAssignmentTypesQuery(
    { affiliateCode: userAffiliateCode },
    {
      skip: !selectedABOs.length,
    }
  );

  const groupType = selectedRole?.code
    ? PermissionGroupTypeMapByRole[String(selectedRole.code)]
    : '';

  const { data: assignees } = useGetAssigneeListQuery(
    {
      affiliateCode: userAffiliateCode,
      typeCode: groupType,
    },
    {
      skip: !groupType || !selectedABOs.length,
    }
  );

  const assigneesList = useMemo<DropdownItem[]>(() => {
    if (!assignees?.length) return [];
    return assignees.map<DropdownItem>((assignee) => ({
      code: assignee.nativeId,
      value: assignee.fullName,
    }));
  }, [assignees]);

  const [assignABO, { isLoading }] = useAssignABOMutation();

  const [toastConfig, setToastConfig] = useState({
    message: '',
    type: '' as toastMessageType,
    show: false,
  });

  useEffect(() => {
    if (toastConfig.show) {
      setTimeout(() => {
        onAssignCallback();
      }, 3000);
    }
  }, [toastConfig]);

  const setShowToast = (show: boolean) => setToastConfig((prev) => ({ ...prev, show }));

  const onAssign = () => {
    if (!selectedRole?.code || !selectedAssignee) return;
    assignABO({
      assignmentType: String(selectedRole?.code),
      nativeId: String(selectedAssignee?.code),
      abos: selectedABOs,
    })
      .unwrap()
      .then(() => {
        setSelectedRole(undefined);
        setSelectedAssignee(null);
        resetSelection();
        navigate('/assignment', {
          replace: true,
          state: {
            successMessage: `[${selectedRole?.value}][${selectedAssignee?.value}] ${t('success_assign_message')}`,
          },
        });
      })
      .catch(() => {
        setToastConfig({
          type: 'error',
          message: t('fail_assign_message'),
          show: true,
        });
      });
  };

  useEffect(() => {
    if (!assignmentTypes?.length) return;

    if (role) {
      const roleItem = assignmentTypes.find((r) => r.code === role);

      if (roleItem) {
        setSelectedRole(roleItem);
      }
    }
  }, [role, assignmentTypes]);

  const filteredRoleList = useMemo(() => {
    if (!assignmentTypes?.length) return [];
    const apiCodes = assignmentTypes.map((type) => Number(type.code));
    return roleListMock.filter((role) => apiCodes.includes(Number(role.code)));
  }, [assignmentTypes]);

  const translatedSelectedRole = useMemo(() => {
    if (!selectedRole) return undefined;
    return roleListMock.find((role) => Number(role.code) === Number(selectedRole.code));
  }, [selectedRole]);

  return (
    <>
      {!!selectedABOs?.length && !!isAccessibleAssignmentSection && (
        <div className={`bulk_assignment_view_container ${styles.container}`}>
          <p>
            {t('selected_accounts_message.prefix')}{' '}
            <span className={styles.bold}>
              {t('selected_accounts_message.highlight', {
                count: selectedABOs.length,
              })}
            </span>{' '}
            {t('selected_accounts_message.suffix')}
          </p>
          <div className={styles.role_dropdown_wrapper}>
            <Dropdown
              dropdownName={t('select_role')}
              items={filteredRoleList}
              selectedItem={translatedSelectedRole}
              handleSelectItemCallback={setSelectedRole}
              className={styles.bulk_dropdown}
              size="sm"
            />
          </div>
          <div className={styles.flex_wrapper}>
            <div className={styles.assignee_dropdown_wrapper}>
              <SearchDropdown
                dropdownName={t('select_assignee')}
                items={assigneesList}
                selectedItem={selectedAssignee}
                handleSelectItemCallback={setSelectedAssignee}
                size="sm"
              />
            </div>
            <Button
              className={styles.assign_btn}
              label={t('assign')}
              onClick={onAssign}
              style="filled"
              isDisabled={!(selectedRole && selectedAssignee) || isLoading}
            />
          </div>
        </div>
      )}
      <ToastMessage
        message={toastConfig.message}
        toastType={toastConfig.type}
        showToast={toastConfig.show}
        setShowToast={setShowToast}
        customStyles={{
          top: '96px',
          maxWidth: '392px',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
        autoDismissal
      />
    </>
  );
};

export default BulkAssignmentView;
